import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import DraggableDialog from 'packages/workspace/design/widgets/ui/DraggableDialog';
import { taskRouterStore } from 'packages/workspace/services/store/omnichannel/taskrouter-store';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { MdAddCircleOutline, MdSend } from 'react-icons/md';
import useDebouncedValue from '../../../modules/helpers/hooks/useDebouncedValue';
import useIsTyping from '../../../modules/helpers/hooks/useIsTyping';
import ButtonWithIcon from '../../common/ButtonWithIcon';

export interface MessageBoxProps {
  sendMessageProp: (msg: string) => void;
  onTyping?: (isTyping: boolean) => void;
  active_message?: string;
  show_notepad?: boolean;
  task_sid?: string;
  notepad_val?: string;
  QuickLinks?: React.FC;
  placeHolderText?: string;
  inputFieldRef?: MutableRefObject<HTMLInputElement | undefined>;
}

const MessageBox: React.FC<MessageBoxProps> = (props) => {
  const { sendMessageProp, onTyping, show_notepad = false, task_sid = '', active_message = '', notepad_val = '', QuickLinks, placeHolderText = 'Type your message here...', inputFieldRef } = props;
  const [message, setMessage] = useState<string>('');
  const [isDialogOpen, SetDialogOpen] = useState<boolean>(false);
  const isTyping = useIsTyping(message, 3000);
  const [userIsTyping, setUserIsTyping] = useState(isTyping);
  const isInitialMount = useRef(true);
  const [note, setNote] = useState(notepad_val);
  const updatedNoteValue = useDebouncedValue(note, 1500);

  useEffect(() => {
    if (task_sid) {
      taskRouterStore.updateNotepadContent(updatedNoteValue, task_sid);
    }
  }, [task_sid, updatedNoteValue]);

  useEffect(() => {
    if (!isInitialMount.current) {
      setUserIsTyping(isTyping);
    }
  }, [isTyping]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      onTyping?.(userIsTyping);
    }
  }, [onTyping, userIsTyping]);

  const sendMessage = useCallback(() => {
    if (message === '') return;
    sendMessageProp(message);
    setMessage('');
    setUserIsTyping(false);
  }, [message, sendMessageProp]);

  useEffect(() => setMessage((prev) => prev + active_message), [active_message]);

  /**
   * @description: Open Draggable Notepad
   */
  const openNotepad = () => {
    SetDialogOpen(true);
  };

  return (
    <>
      <Dialog
        open={isDialogOpen}
        //onClose={() => SetDialogOpen(false)}
        PaperComponent={DraggableDialog}
        hideBackdrop
        disableEnforceFocus
        style={{ pointerEvents: 'none' }}
        PaperProps={{ style: { pointerEvents: 'auto' } }}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          Notepad
        </DialogTitle>
        <DialogContent>
          <DialogContentText>Use This As Notepad Area</DialogContentText>
          <TextField id="outlined-multiline-static" onChange={(ev) => setNote(ev.target.value)} label="Notes" multiline value={note} rows={8} placeholder="Enter Notes Here" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => SetDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <div className="w-full px-3 py-2 bg-tc-1 border-t border-b border-tc-6 flex justify-between items-center">
        <TextField
          inputRef={inputFieldRef}
          className="rhs-chat-input"
          placeholder={placeHolderText}
          value={message}
          multiline
          onChange={(ev) => {
            setMessage(ev.target.value);
          }}
          onKeyPress={(ev) => {
            if (ev.key === 'Enter' && message.trim() !== '' && !ev.shiftKey) {
              sendMessage();
              ev.preventDefault();
            }
          }}
        />
        {QuickLinks && <QuickLinks />}
        {show_notepad && (
          <ButtonWithIcon
            tooltipProps={{
              title: 'Notepad',
              placement: 'top',
            }}
            tooltip="Notepad"
            Icon={MdAddCircleOutline}
            onClick={() => openNotepad()}
            className="text-tc-4 text-base"
          />
        )}
        <ButtonWithIcon
          tooltipProps={{
            title: 'Send Message',
            placement: 'top',
          }}
          Icon={MdSend}
          tooltip="Send Message"
          className="text-tc-4 text-base"
          onClick={sendMessage}
          disabled={message === ''}
          aria-label="Send Message"
        />
      </div>
    </>
  );
};

export default MessageBox;
